import { useState, useEffect } from 'react'
import { LogOut, ChevronRight, X } from 'lucide-react'

const LIVE_MS = 120_000
function agoText(ageMs: number): string {
    const m = Math.floor(ageMs / 60_000)
    if (m < 60) return `${m} min ago`
    const h = Math.floor(m / 60)
    if (h < 48) return `${h} h ago`
    return `${Math.floor(h / 24)} d ago`
}
import { useApp } from '../../store/AppContext'
import { fetchAvailableBoats, subscribeLiveData, subscribeAllLive } from '../../firebase/boatData'
import { getToken } from '../../api/auth'

const API_URL = (import.meta as any).env.VITE_API_URL ?? ''
import { clearToken } from '../../api/auth'

export default function Sidebar() {
    const {
        currentSchool,
        setCurrentSchool,
        availableBoats,
        setAvailableBoats,
        selectedBoats,
        toggleBoatSelection,
        updateLiveData,
        updateAllBoatsLiveData,
        allBoatsLiveData,
        sidebarOpen,
        setSidebarOpen,
        refreshTrigger,
        sourceVersion
    } = useApp()

    const [loading, setLoading] = useState(false)
    // "Live first" is the default — a fleet monitor should read top-down by who is on the water NOW.
    const [sortBy, setSortBy] = useState<'recent' | 'name'>('recent')
    // Which sensors each boat carried, and when it was last active — fetched from the
    // server's maintained per-boat profile, so telling boats apart in a 70-long list
    // costs one small query rather than reading anyone's track.
    const [meta, setMeta] = useState<Record<string, { sensors: string[]; lastSeen: number | null; fixes: number | null }>>({})
    // Restrict the list to boats that actually recorded on a given day. Empty = all time.
    const [onDate, setOnDate] = useState('')
    const [, setTick] = useState(0)
    useEffect(() => {                                   // ages keep counting even with no new data
        const id = setInterval(() => setTick(t => t + 1), 30_000)
        return () => clearInterval(id)
    }, [])

    // Load boats on mount and when refresh is triggered
    useEffect(() => {
        if (currentSchool) {
            loadBoats()
        }
    }, [currentSchool, refreshTrigger])

    // Subscribe to selected boats (for dashboard)
    useEffect(() => {
        if (!currentSchool) return

        const unsubscribes: Array<() => void> = []

        selectedBoats.forEach(boatId => {
            const unsub = subscribeLiveData(boatId, (data) => {
                updateLiveData(boatId, data)
            })
            unsubscribes.push(unsub)
        })

        return () => {
            unsubscribes.forEach(unsub => unsub())
        }
    }, [selectedBoats, currentSchool, sourceVersion, refreshTrigger])

    // Subscribe to ALL boats for live banner + map ghost markers
    useEffect(() => {
        if (!currentSchool || availableBoats.length === 0) return

        // ghosts = "who's transmitting now": ONE batch poll of our server for every boat (Firebase per boat only
        // as the fallback) — a live Group session never reaches Firebase, so a Firebase-only layer hid it
        return subscribeAllLive(availableBoats.map(b => b.id), (id, data) => updateAllBoatsLiveData(id, data))
    }, [availableBoats, currentSchool, refreshTrigger, sourceVersion])

    async function loadBoats() {
        setLoading(true)

        try {
            const boats = await fetchAvailableBoats()

            if (boats.length === 0) {
                alert('No boats found in database')
                setLoading(false)
                return
            }

            setAvailableBoats(boats.map(id => ({
                id,
                name: id,
                isActive: true
            })))

            // Non-fatal: the list still works without it, just without badges.
            loadMeta().catch(() => {})
            setLoading(false)
        } catch (err: any) {
            alert('Failed to load boats: ' + err.message)
            setLoading(false)
        }
    }

    /** Per-boat sensor kinds + activity, optionally restricted to one calendar day. */
    async function loadMeta(dayIso?: string) {
        const token = getToken()
        if (!token) return
        let q = ''
        if (dayIso) {
            // A whole local day, so "boats that went out on the 1st" means what a person
            // means by it — not a UTC window that clips the evening.
            const from = new Date(`${dayIso}T00:00:00`).getTime()
            const to = new Date(`${dayIso}T23:59:59.999`).getTime()
            q = `?from=${from}&to=${to}`
        }
        const r = await fetch(`${API_URL}/api/boats/detailed${q}`, { headers: { Authorization: `Bearer ${token}` } })
        if (!r.ok) return
        const { boats } = await r.json()
        const next: Record<string, { sensors: string[]; lastSeen: number | null; fixes: number | null }> = {}
        for (const b of boats) next[b.boatId] = { sensors: b.sensors ?? [], lastSeen: b.lastSeen ?? null, fixes: b.fixes ?? null }
        setMeta(next)
    }

    // With a date filter on, `meta` IS the answer set (the server returned only boats
    // active in that window), so the list narrows to it. Without one, show everything.
    const visibleBoats = onDate ? availableBoats.filter(b => meta[b.id]) : availableBoats

    function applyDate(v: string) {
        setOnDate(v)
        loadMeta(v || undefined).catch(() => {})
    }

    function handleLogout() {
        clearToken()
        setCurrentSchool(null)
        setAvailableBoats([])
    }

    if (!sidebarOpen) {
        return (
            <button
                onClick={() => setSidebarOpen(true)}
                className="absolute top-4 left-4 z-10 glass px-3 py-2 rounded-lg hover:bg-white/90 transition-all"
            >
                <ChevronRight className="w-5 h-5" />
            </button>
        )
    }

    return (
        <div className="absolute top-4 left-4 z-10 w-80 glass-dark rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex-1">
                    <h2 className="font-semibold text-white">{currentSchool?.name || 'School'}</h2>
                    <p className="text-xs text-white/60 mt-1">Boat Selection</p>
                </div>
                <button
                    onClick={() => setSidebarOpen(false)}
                    className="text-white/60 hover:text-white transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
                {loading ? (
                    <div className="text-center text-white/60 py-8">Loading boats...</div>
                ) : (
                    <>
                        {/* Boat List */}
                        <div>
                            {/* Filter by the day a boat actually recorded. With 70 boats in the
                                list, "which ones went out on the 1st" is the question people
                                actually have, and it cannot be answered by name or by liveness. */}
                            <div className="flex items-center gap-2 mb-2">
                                <input
                                    type="date"
                                    value={onDate}
                                    onChange={e => applyDate(e.target.value)}
                                    title="Show only boats that recorded on this day"
                                    className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs"
                                    style={{ colorScheme: 'dark' }}
                                />
                                {onDate && (
                                    <button onClick={() => applyDate('')}
                                        className="px-2 py-1 rounded bg-white/5 text-white/50 hover:text-white text-xs"
                                        title="Show all boats again">clear</button>
                                )}
                            </div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-white/80">
                                    {onDate ? `Boats on ${onDate}` : 'Available Boats'} ({visibleBoats.length})
                                </label>
                                <div className="flex gap-1 text-[10px]">
                                    <button onClick={() => setSortBy('recent')}
                                        className={sortBy === 'recent' ? 'px-2 py-0.5 rounded bg-green-500/30 text-green-300' : 'px-2 py-0.5 rounded bg-white/5 text-white/50 hover:text-white/80'}>
                                        Live first
                                    </button>
                                    <button onClick={() => setSortBy('name')}
                                        className={sortBy === 'name' ? 'px-2 py-0.5 rounded bg-green-500/30 text-green-300' : 'px-2 py-0.5 rounded bg-white/5 text-white/50 hover:text-white/80'}>
                                        A–Z
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {[...visibleBoats].sort((a, b) => {
                                    if (sortBy === 'name') return a.name.localeCompare(b.name)
                                    const ta = allBoatsLiveData.get(a.id)?.timestamp ?? 0
                                    const tb = allBoatsLiveData.get(b.id)?.timestamp ?? 0
                                    return tb - ta || a.name.localeCompare(b.name)
                                }).map(boat => (
                                    <label
                                        key={boat.id}
                                        className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-all"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedBoats.includes(boat.id)}
                                            onChange={() => toggleBoatSelection(boat.id)}
                                            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 focus:ring-offset-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-medium text-sm truncate">{boat.name}</div>
                                            {(() => {
                                                // Prefer the live feed's timestamp; fall back to the server's
                                                // record, so a boat that is merely OLD still says when it sailed
                                                // instead of "no data".
                                                const ts = allBoatsLiveData.get(boat.id)?.timestamp ?? meta[boat.id]?.lastSeen ?? undefined
                                                if (!ts) return <div className="text-white/30 text-xs">no data</div>
                                                const age = Date.now() - ts
                                                return age < LIVE_MS
                                                    ? <div className="text-green-400 text-xs flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />LIVE now
                                                      </div>
                                                    : <div className="text-white/40 text-xs">{agoText(age)}</div>
                                            })()}
                                            {/* Sensor badges — the fastest way to tell a plain tracker phone
                                                from the boat carrying the water probes. */}
                                            {(() => {
                                                const s = meta[boat.id]?.sensors ?? []
                                                if (!s.length) return null
                                                const ICON: Record<string, [string, string]> = {
                                                    ph: ['🧪', 'pH'], o2: ['💧', 'Dissolved oxygen'], tds: ['⚡', 'Dissolved solids'],
                                                    turbidity: ['🌫', 'Turbidity / clarity'], water_temp: ['🌡', 'Water temperature'],
                                                    wind: ['💨', 'Wind'], nh4: ['🧫', 'Ammonium'],
                                                }
                                                return (
                                                    <div className="flex gap-1 mt-1 flex-wrap" title={`Sensors used: ${s.join(', ')}`}>
                                                        {s.map(k => (
                                                            <span key={k} title={ICON[k]?.[1] ?? k}
                                                                className="text-[10px] leading-none px-1 py-0.5 rounded bg-cyan-500/15 border border-cyan-400/25">
                                                                {ICON[k]?.[0] ?? '•'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )
                                            })()}
                                        </div>
                                        {meta[boat.id]?.fixes != null && (
                                            <span className="text-[10px] text-white/30 tabular-nums shrink-0"
                                                title="Position fixes recorded in the selected day">
                                                {meta[boat.id]!.fixes!.toLocaleString()}
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {selectedBoats.length > 0 && (
                            <div className="text-xs text-white/60 bg-primary-500/10 border border-primary-500/20 rounded-lg p-3">
                                {selectedBoats.length} boat{selectedBoats.length > 1 ? 's' : ''} selected
                            </div>
                        )}

                        {/* Connection Info */}
                        <div className="text-xs text-white/40 bg-black/20 rounded p-2 mb-2 break-all">
                            <div className="font-semibold mb-1">Connected to:</div>
                            {currentSchool?.firebaseUrl}
                        </div>

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20 rounded-lg font-medium transition-all"
                        >
                            <LogOut className="w-4 h-4" />
                            Logout
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}
